/**
 * A table as a real .xlsx, without a spreadsheet library.
 *
 * CSV was the obvious answer and it is the wrong one for THIS data: a postal code is the column an
 * address report exists for, and Excel reads `01234` out of a CSV as the number 1234. Every New England
 * customer would arrive with a broken ZIP, silently, in the file someone is about to post something to.
 * Quoting does not help — Excel converts anyway — and the `="01234"` trick is an injection hazard dressed
 * up as a fix.
 *
 * So the cells are written as text and the file is a genuine OOXML package. That package is a ZIP holding
 * six small XML parts, and a ZIP of STORED (uncompressed) entries is a header, the bytes, and a central
 * directory — about eighty lines. That is a smaller and far more auditable dependency than any of the
 * spreadsheet libraries, each of which weighs more than the rest of this page put together.
 *
 * Everything is a string cell, deliberately. No report here has a column worth summing, and "text unless
 * asked otherwise" is the behaviour that keeps an identifier intact. Add number cells when something
 * actually needs arithmetic, not before.
 */

import type { Column } from './csv';

const enc = new TextEncoder();

const xml = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * Strip what XML 1.0 cannot carry at all.
 *
 * A stray control character in a customer's name makes the whole workbook unopenable — Excel calls it
 * corrupt and names no cell — so these are dropped rather than escaped. Tab, newline and carriage return
 * are legal and are kept.
 */
const clean = (s: string): string => s.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '');

/** A1, B1 … Z1, AA1. */
export function columnLetter(index: number): string {
  let n = index + 1;
  let out = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    out = String.fromCharCode(65 + rem) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
}

/**
 * Excel refuses a workbook whose sheet name carries any of `[]:*?/\`, is empty, or runs past 31
 * characters — and refuses it as "unreadable content", naming nothing. A report title is written by a
 * person, so it is cleaned here rather than trusted.
 */
export function safeSheetName(name: string): string {
  const cleaned = name.replace(/[[\]:*?/\\]/g, ' ').trim().slice(0, 31);
  return cleaned || 'Sheet1';
}

// ---------------------------------------------------------------- ZIP

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[i] = c >>> 0;
  }
  return table;
})();

export function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]!) & 0xff]! ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

/** MS-DOS date and time, which is what a ZIP entry carries. Seconds have two-second resolution. */
function dosStamp(at: Date): { time: number; date: number } {
  return {
    time: (at.getHours() << 11) | (at.getMinutes() << 5) | (Math.floor(at.getSeconds() / 2) & 0x1f),
    date: ((at.getFullYear() - 1980) << 9) | ((at.getMonth() + 1) << 5) | at.getDate(),
  };
}

interface Part { name: string; bytes: Uint8Array }

/**
 * A ZIP with no compression.
 *
 * Stored rather than deflated because the browser has no synchronous deflate and these parts are tens of
 * kilobytes of XML. The file is larger than it needs to be and nobody will notice; pulling in a deflate
 * implementation to shrink a download that already takes no time is the wrong trade.
 */
function zip(parts: readonly Part[], at: Date): Uint8Array<ArrayBuffer> {
  const { time, date } = dosStamp(at);
  const locals: Uint8Array[] = [];
  const centrals: Uint8Array[] = [];
  let offset = 0;

  for (const part of parts) {
    const name = enc.encode(part.name);
    const crc = crc32(part.bytes);

    const local = new Uint8Array(30 + name.length);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, 0x04034b50, true);
    lv.setUint16(4, 20, true); // version needed
    lv.setUint16(6, 0, true); // flags
    lv.setUint16(8, 0, true); // method: stored
    lv.setUint16(10, time, true);
    lv.setUint16(12, date, true);
    lv.setUint32(14, crc, true);
    lv.setUint32(18, part.bytes.length, true);
    lv.setUint32(22, part.bytes.length, true);
    lv.setUint16(26, name.length, true);
    lv.setUint16(28, 0, true); // extra
    local.set(name, 30);

    const central = new Uint8Array(46 + name.length);
    const cv = new DataView(central.buffer);
    cv.setUint32(0, 0x02014b50, true);
    cv.setUint16(4, 20, true); // version made by
    cv.setUint16(6, 20, true); // version needed
    cv.setUint16(8, 0, true);
    cv.setUint16(10, 0, true);
    cv.setUint16(12, time, true);
    cv.setUint16(14, date, true);
    cv.setUint32(16, crc, true);
    cv.setUint32(20, part.bytes.length, true);
    cv.setUint32(24, part.bytes.length, true);
    cv.setUint16(28, name.length, true);
    cv.setUint16(30, 0, true); // extra
    cv.setUint16(32, 0, true); // comment
    cv.setUint16(34, 0, true); // disk
    cv.setUint16(36, 0, true); // internal attrs
    cv.setUint32(38, 0, true); // external attrs
    cv.setUint32(42, offset, true);
    central.set(name, 46);

    locals.push(local, part.bytes);
    centrals.push(central);
    offset += local.length + part.bytes.length;
  }

  const centralSize = centrals.reduce((n, c) => n + c.length, 0);
  const end = new Uint8Array(22);
  const ev = new DataView(end.buffer);
  ev.setUint32(0, 0x06054b50, true);
  ev.setUint16(8, parts.length, true);
  ev.setUint16(10, parts.length, true);
  ev.setUint32(12, centralSize, true);
  ev.setUint32(16, offset, true);

  const chunks = [...locals, ...centrals, end];
  const out = new Uint8Array(chunks.reduce((n, c) => n + c.length, 0));
  let cursor = 0;
  for (const c of chunks) {
    out.set(c, cursor);
    cursor += c.length;
  }
  return out;
}

// ---------------------------------------------------------------- OOXML

const CONTENT_TYPES = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
</Types>`;

const ROOT_RELS = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;

const WORKBOOK_RELS = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`;

/*
 * One bold font for the header row, and nothing else. Both fills are present because Excel expects the
 * first two to be `none` and `gray125` and treats their absence as a damaged file.
 */
const STYLES = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>
<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>
<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/></cellXfs>
<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`;

const cell = (ref: string, text: string, bold: boolean): string =>
  `<c r="${ref}"${bold ? ' s="1"' : ''} t="inlineStr"><is><t xml:space="preserve">${xml(clean(text))}</t></is></c>`;

/** Wide enough to read without the sheet scrolling; capped, because an address can be very long. */
function width(header: string, values: readonly string[]): number {
  const longest = values.reduce((n, v) => Math.max(n, v.length), header.length);
  return Math.min(Math.max(longest + 2, 10), 46);
}

export interface SheetOptions {
  /** Shown on the tab. Cleaned to what Excel will accept. */
  sheetName?: string;
  /** Stamped on the ZIP entries. Passed in so the output is reproducible under test. */
  now?: Date;
}

/** The workbook, as bytes ready to be saved. */
export function toXlsx<T>(
  rows: readonly T[],
  columns: readonly Column<T>[],
  opts: SheetOptions = {},
): Uint8Array<ArrayBuffer> {
  const cells = rows.map((r) => columns.map((c) => c.value(r)));

  const cols = columns
    .map((c, i) => {
      const w = width(c.header, cells.map((r) => r[i] ?? ''));
      return `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`;
    })
    .join('');

  const header = `<row r="1">${columns.map((c, i) => cell(`${columnLetter(i)}1`, c.header, true)).join('')}</row>`;
  const body = cells
    .map(
      (values, r) =>
        `<row r="${r + 2}">${values.map((v, i) => cell(`${columnLetter(i)}${r + 2}`, v, false)).join('')}</row>`,
    )
    .join('');

  const lastCol = columnLetter(Math.max(columns.length - 1, 0));
  /*
   * A frozen header and a filter, because this is a list someone reads and sorts. `autoFilter` has to name
   * the whole used range: Excel takes the range literally, and a short one leaves the later rows out of
   * the filter without saying so.
   */
  const sheet = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>
<cols>${cols}</cols>
<sheetData>${header}${body}</sheetData>
<autoFilter ref="A1:${lastCol}${rows.length + 1}"/>
</worksheet>`;

  const workbook = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheets><sheet name="${xml(safeSheetName(opts.sheetName ?? 'Report'))}" sheetId="1" r:id="rId1"/></sheets>
</workbook>`;

  return zip(
    [
      { name: '[Content_Types].xml', bytes: enc.encode(CONTENT_TYPES) },
      { name: '_rels/.rels', bytes: enc.encode(ROOT_RELS) },
      { name: 'xl/workbook.xml', bytes: enc.encode(workbook) },
      { name: 'xl/_rels/workbook.xml.rels', bytes: enc.encode(WORKBOOK_RELS) },
      { name: 'xl/styles.xml', bytes: enc.encode(STYLES) },
      { name: 'xl/worksheets/sheet1.xml', bytes: enc.encode(sheet) },
    ],
    opts.now ?? new Date(),
  );
}
